import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { apiService } from '../services/api';
import { cognitoAuthService } from '../services/cognitoAuth';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if user is authenticated
      const authenticated = await cognitoAuthService.isAuthenticated();
      setIsAuthenticated(authenticated);

      if (!authenticated) {
        setUser(null);
        setLoading(false);
        return;
      }

      // Fetch user profile
      const userProfile = await apiService.getUserProfile();
      setUser(userProfile);
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError('Failed to load user profile');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const refetch = async () => {
    await fetchUserProfile();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        refetch,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
